import * as React from "react";
import { useHubx } from "../../HubX";
import CustomerModel from "../../core/models/Customer";
import useConstructor from "../../core/hooks/useConstructor";
import { DO_NOTIFICATION_READED, FETCH_NOTIFICATION_UNREAD } from "../../Fedx";

import { isArrayValid, isNumberValid } from "../../helpers/utility";
import NotificationModel from "../../core/models/Notification";
import { SessionCache } from "../../helpers/cache";
import NotificationDetailModel from "../../core/models/NotificationDetail";
import INotificationReaded from "../../core/contracts/INotificationReaded";
import NotificationComponent from "../../components/NotificationComponent";
import { APP_DEFAULT_NUMBER_OF_NOTIFICATIONS } from "../../core/constants/appGlobals";

export type HomePageViewType = "card" | "table";
export type CustomerAccordionType = { [id: string]: boolean } | null;

type Notification = {
  notifications?: NotificationModel[];
};

function Notification() {
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [notifications, SetNotifications] = React.useState<NotificationDetailModel[]>([]);
  const [totalNotifications, setTotal] = React.useState<number>(0);
  const [isLoading, setLoading] = React.useState(false);

  const hub = useHubx();

  const getNotifications = async (pageSize = APP_DEFAULT_NUMBER_OF_NOTIFICATIONS) => {
    try {
      setLoading(true);
      const APIResponse: NotificationModel = await hub.request(FETCH_NOTIFICATION_UNREAD, {
        page: currentPage,
        pageSize: pageSize,
      });
      setTotal(APIResponse.totalNotification);
      SetNotifications(
        APIResponse.notifications
          ? [...notifications, ...APIResponse.notifications]
          : [...notifications]
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Notification → getNotifications -> error", error);
    } finally {
      setLoading(false);
    }
  };

  useConstructor(async () => {
    await getNotifications();
  });

  const handleNotificationClick = async (idNotification?: number, clearAll = false) => {
    const notificationReaded: INotificationReaded = {
      idNotification: idNotification,
      clearAllNotification: clearAll,
    };

    if (clearAll) {
      SetNotifications([]);
    }

    await hub.request(DO_NOTIFICATION_READED, notificationReaded);

    if (!clearAll) {
      const newNumberOfNotifications =
        notifications?.length - 1 > 10 ? notifications.length : APP_DEFAULT_NUMBER_OF_NOTIFICATIONS;

      SetNotifications([]);
      setCurrentPage(1);
      getNotifications(newNumberOfNotifications);
    }
  };

  const getInstallation = (idInstallation: string, field: string): string | number => {
    const storage = new SessionCache();
    const cachedClients: CustomerModel[] | null = storage.get("customers");

    if (isArrayValid(cachedClients)) {
      const cachedInstallations = cachedClients
        ?.map((e) => e.installations)
        ?.reduce((prev, current) => prev?.concat(current ? current : []), []);

      if (cachedInstallations) {
        const objInstallation = cachedInstallations?.find((i) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          return i.installation_id === idInstallation;
        });

        if (field === "name") {
          return objInstallation ? (objInstallation?.product_code as string) : "";
        } else if (field === "level") {
          return objInstallation ? (objInstallation?.installation_level as number) : 0;
        } else if (field === "capacity") {
          return objInstallation ? (objInstallation?.inventory_capacity_su as number) : 0;
        }
      }
    }

    return "";
  };

  const getGroupInstallation = (idInstallation: string) => {
    const storage = new SessionCache();
    const cachedClients: CustomerModel[] | null = storage.get("customers");

    if (isArrayValid(cachedClients)) {
      const objInstallationGroup = cachedClients?.find((g) =>
        g.installations?.find((i) => {
          return i.installation_id === idInstallation;
        })
      );
      return objInstallationGroup ? objInstallationGroup?.customer_name : "";
    }
  };

  const shouldShowRenderMore = React.useMemo(() => {
    return (
      isArrayValid(notifications) &&
      isNumberValid(totalNotifications) &&
      totalNotifications > 0 &&
      notifications.length < totalNotifications
    );
  }, [notifications, totalNotifications]);

  const handleClickShowMore = () => {
    setCurrentPage((prev) => prev++);
    getNotifications();
  };

  return (
    <NotificationComponent
      notifications={notifications}
      handleClickNotification={handleNotificationClick}
      getInstallation={getInstallation}
      getGroupInstallation={getGroupInstallation}
      handleClickShowMore={handleClickShowMore}
      shouldShowRenderMore={shouldShowRenderMore}
      totalNotifications={totalNotifications}
      loading={isLoading}
    />
  );
}

export default Notification;
